import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSyncedCart } from "../../hooks/useSyncedCart";
import { openCartDrawer } from "../../utils/cartDrawerEvents";
import { buildCartEventPayload, META_PIXEL_EVENTS, trackMetaEvent } from "../../utils/metaPixel";
import "./HomePage.css";

function normalizeVariants(product) {
  if (Array.isArray(product?.weights) && product.weights.length > 0) {
    return product.weights.map((weight, index) => {
      if (typeof weight === "string") {
        return {
          id: weight,
          label: weight,
          price: product?.price ?? 0,
        };
      }

      return {
        id:
          weight.id ||
          weight.label ||
          `${product?.slug || product?.product_id || "product"}-${index}`,
        label: weight.label || weight.weight || String(weight.id || index + 1),
        product_id: weight.product_id || product?.product_id,
        price:
          weight.price ??
          weight.product_price ??
          product?.product_price ??
          weight.product_sell_price ??
          product?.price ??
          product?.product_sell_price ??
          0,
        originalPrice:
          weight.product_sell_price ??
          product?.product_sell_price ??
          0,
        discount: weight.discount ?? product?.discount ?? product?.product_discount ?? 0,
      };
    });
  }

  if (Array.isArray(product?.variant_data) && product.variant_data.length > 0) {
    return product.variant_data.map((variant, index) => ({
      id: `${product?.product_id || product?.slug || "product"}-${
        variant?.product_id || index
      }-${variant?.weight || variant?.item_weight || index}`,
      label: String(variant?.weight || variant?.item_weight || `Pack ${index + 1}`),
      product_id: variant?.product_id || product?.product_id,
      price:
        variant?.product_price ??
        product?.product_price ??
        variant?.product_sell_price ??
        product?.price ??
        product?.product_sell_price ??
        0,
      originalPrice:
        variant?.product_sell_price ??
        product?.product_sell_price ??
        0,
      discount: variant?.discount ?? product?.discount ?? product?.product_discount ?? 0,
    }));
  }

  return [];
}

function formatPrice(price) {
  const numericPrice = Number.parseFloat(price);

  if (Number.isFinite(numericPrice)) {
    return Intl.NumberFormat("en-IN").format(numericPrice);
  }

  return price || "0";
}

function FeaturedProductCard({ product }) {
  const { addProduct, items: cartItems, updateQuantity } = useSyncedCart();
  const title = product?.title || product?.product_name || "Kuku Product";
  const productLink = product?.slug ? `/product/${product.slug}` : "/categories";
  const imageSrc =
    product?.img ||
    product?.image ||
    product?.thumbnail_image ||
    "/kuku-assets/products/dry-chapti-kachori/1 chapti kachori.jpg";
  const badge = product?.badge || product?.label_badge;
  const variants = normalizeVariants(product);
  const initialVariantId = variants[0]?.id || null;

  const [qty, setQty] = useState(1);
  const [selectedWeight, setSelectedWeight] = useState(initialVariantId);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    setQty(1);
    setSelectedWeight(initialVariantId);
  }, [product?.slug, initialVariantId]);

  const activeVariant =
    variants.find((variant) => variant.id === selectedWeight) || variants[0];
  const activePrice =
    activeVariant?.price ??
    product?.product_price ??
    product?.price ??
    product?.product_sell_price ??
    0;
  const originalPrice =
    activeVariant?.originalPrice ??
    product?.product_sell_price ??
    0;
  const showOriginalPrice = Number(originalPrice) > Number(activePrice);
  const priceLabel = `\u20b9${formatPrice(activePrice)}/-`;
  const activeProductId = activeVariant?.product_id || product?.product_id || product?.id;
  const cartItem = cartItems.find(
    (item) => String(item.product_id) === String(activeProductId)
  );
  const cartQuantity = Number(cartItem?.quantity || 0);
  const isCartQuantityChanged = Boolean(cartItem) && qty !== cartQuantity;
  const isCartButtonDisabled = isAdding || (Boolean(cartItem) && !isCartQuantityChanged);
  const cartButtonLabel = cartItem && !isCartQuantityChanged ? "Added to cart" : "Add to cart";

  useEffect(() => {
    setQty(cartItem ? cartQuantity : 1);
  }, [activeProductId, cartItem, cartQuantity]);

  const handleDecreaseQty = () => {
    setQty((current) => Math.max(cartItem ? 0 : 1, current - 1));
  };

  const handleIncreaseQty = () => {
    setQty((current) => current + 1);
  };

  const handleAddToCart = async () => {
    try {
      setIsAdding(true);
      if (cartItem) {
        await updateQuantity(activeProductId, qty);
        trackMetaEvent(
          META_PIXEL_EVENTS.ADD_TO_CART,
          buildCartEventPayload([
            {
              product_id: activeProductId,
              product_name: title,
              price: activePrice,
              quantity: qty,
            },
          ])
        );
        openCartDrawer();
        return;
      }

      await addProduct(product, qty, activeVariant);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="fp-card">
      {badge && (
        <div className="hp-badge-wrap">
          <span className="hp-badge">{badge}</span>
        </div>
      )}

      <div className="fp-card-img-wrap">
        <Link to={productLink}>
          <img
            src={imageSrc}
            alt={title}
            className="fp-card-img"
          />
        </Link>
      </div>

      <div className="fp-card-body">
        <h5 className="fp-card-title">
          <Link to={productLink}>{title}</Link>
        </h5>

        <div className="fp-usp-tabs" aria-label="Product highlights">
          <span className="fp-usp-badge">No Palm Oil</span>
          <span className="fp-usp-badge">No Added Preservatives</span>
          <span className="fp-usp-badge">No Added Colors</span>
          <span className="fp-usp-badge">No Onion/Garlic</span>
        </div>

        <div className="fp-price-row">
          <div className="fp-price-stack">
            <span className="fp-price">{priceLabel}</span>
            {showOriginalPrice && (
              <span className="fp-price-original">
                ₹{formatPrice(originalPrice)}/-
              </span>
            )}
            
          </div>

          {variants.length > 0 && (
            <div className="fp-weight-btns">
              {variants.map((variant) => (
                <button
                  key={variant.id}
                  type="button"
                  onClick={() => setSelectedWeight(variant.id)}
                  className={`fp-weight-btn${
                    selectedWeight === variant.id ? " fp-weight-btn--active" : ""
                  }`}
                >
                  {variant.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="fp-atc-row">
          <div className="fp-qty">
            <button
              type="button"
              className="fp-qty-btn"
              onClick={handleDecreaseQty}
            >
              -
            </button>
            <span className="fp-qty-num">{qty}</span>
            <button
              type="button"
              className="fp-qty-btn"
              onClick={handleIncreaseQty}
            >
              +
            </button>
          </div>

          <button
            type="button"
            className="fp-atc-btn"
            onClick={handleAddToCart}
            disabled={isCartButtonDisabled}
            aria-label={isAdding ? "Adding to cart" : cartButtonLabel}
          >
            {isAdding ? <span className="fp-atc-loader" aria-hidden="true" /> : cartButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FeaturedProductCard;
